import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { Heading } from "react-aria-components";

import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";

export type EnableInviteOnlyModalProps = {
  tournamentId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function EnableInviteOnlyModal({
  tournamentId,
  onSuccess,
  ...props
}: EnableInviteOnlyModalProps) {
  return (
    <Modal {...props} isDismissable>
      <div className="flex max-w-md flex-col space-y-4 p-6">
        <Heading className={title({ size: "sm" })} slot="title">
          Enable Invite-Only
        </Heading>
        <Suspense fallback={<div>Loading...</div>}>
          <EnableInviteOnlyContent
            tournamentId={tournamentId}
            onCancel={() => props.onOpenChange(false)}
            onSuccess={onSuccess}
          />
        </Suspense>
      </div>
    </Modal>
  );
}

function EnableInviteOnlyContent({
  tournamentId,
  onCancel,
  onSuccess,
}: {
  tournamentId: number;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const trpc = useTRPC();

  const { data: tournament } = useSuspenseQuery(
    trpc.tournaments.get.queryOptions({ id: tournamentId }),
  );

  const { mutate, isPending, error } = useMutation({
    ...trpc.tournaments.edit.mutationOptions(),
    onSuccess,
  });

  return (
    <>
      {error && (
        <Alert
          title="Failed to enable invite-only"
          description={error.message}
        />
      )}
      <p>
        This tournament is not currently invite-only. Would you like to enable
        invite-only mode?
      </p>
      <div className="flex flex-row justify-end space-x-2">
        <Button onPress={onCancel}>Cancel</Button>
        <Button
          variant="solid"
          color="primary"
          isPending={isPending}
          onPress={() => {
            if (!tournament) return;
            mutate({
              id: tournamentId,
              date: tournament.date,
              startTime: tournament.startTime,
              venueId: tournament.venueId,
              name: tournament.name,
              visible: tournament.visible,
              inviteOnly: true,
              mergeDivisions: false,
            });
          }}
        >
          Enable
        </Button>
      </div>
    </>
  );
}
